/**
 * WorkflowWaterfall
 *
 * Horizontal waterfall chart showing activity lifecycle bars.
 * X-axis: time from workflow start to end/now
 * Y-axis: rows per activity execution, grouped by type
 * Bars: Scheduled (gray) -> Started (blue) -> Completed (green) / Failed (red)
 * Pipeline event markers grouped by phase (sprint, epic, story, sprite, build, test, ship)
 *
 * Uses container-relative scaling (pxPerMs) instead of fixed px/sec multiplier.
 * Named zoom levels (1m, 5m, 15m, 1h, all) with native overflow-x scrolling.
 */

import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import type { ActivityLifecycle, UnifiedTimelineEvent } from '../../api/telemetry';
import type { IssueExecution } from '../../api/sprint';

interface WorkflowWaterfallProps {
  activityLifecycles: ActivityLifecycle[];
  customEvents?: UnifiedTimelineEvent[];
  workflowStartTime?: string;
  workflowEndTime?: string;
  className?: string;
  selectedActivityId?: string | null;
  onSelectActivity?: (activityId: string | null) => void;
  /** Lookup map: storyId → IssueExecution. Used to enrich tooltip with DB details. */
  issueExecutionMap?: Map<string, IssueExecution>;
  /** Callback when user clicks a story bar — opens detail panel */
  onNavigateToStory?: (storyId: string) => void;
  /** When sprint is finished, pass its status to cap all running bars */
  sprintStatus?: 'building' | 'completed' | 'failed' | 'planning' | 'partial';
  /** The time the sprint ended — used to clip running bars */
  sprintEndTime?: string;
}

/* ── Constants ────────────────────────────────────────────────────── */

const ROW_HEIGHT = 28;
const ROW_GAP = 2;
const LEFT_LABEL_WIDTH = 180;
const TOP_AXIS_HEIGHT = 32;
const PADDING_RIGHT = 16;
const MIN_BAR_WIDTH = 2;

/* ── Zoom Levels ─────────────────────────────────────────────────── */

type ZoomLevel = '1m' | '5m' | '15m' | '1h' | 'all';

const ZOOM_MS: Record<ZoomLevel, number | null> = {
  '1m':  60_000,
  '5m':  5 * 60_000,
  '15m': 15 * 60_000,
  '1h':  60 * 60_000,
  'all': null,
};

/* ── Pipeline Event Groups ────────────────────────────────────────── */

interface EventGroup {
  id: string;
  label: string;
  color: string;
  shape: 'circle' | 'diamond' | 'triangle' | 'square';
  prefixes: string[];
}

const EVENT_GROUPS: EventGroup[] = [
  {
    id: 'sprint',
    label: 'Sprint',
    color: '#3b82f6',  // blue
    shape: 'circle',
    prefixes: ['code_execution_', 'wave_plan_', 'epic_queued', 'epic_quality_gate'],
  },
  {
    id: 'epic',
    label: 'Epic',
    color: '#6366f1',  // indigo
    shape: 'circle',
    prefixes: ['epic_execution_'],
  },
  {
    id: 'story',
    label: 'Story',
    color: '#14b8a6',  // teal
    shape: 'diamond',
    prefixes: ['story_execution_', 'story_repair_'],
  },
  {
    id: 'sprite',
    label: 'Sprite',
    color: '#D97F6F',  // coral
    shape: 'triangle',
    prefixes: ['sprite_', 'claude_'],
  },
  {
    id: 'build',
    label: 'Build',
    color: '#f97316',  // orange
    shape: 'square',
    prefixes: ['build_', 'batch_', 'enrichment_', 'architecture_'],
  },
  {
    id: 'test',
    label: 'Test',
    color: '#8b5cf6',  // violet
    shape: 'triangle',
    prefixes: ['test_', 'verification_', 'post_build_test_', 'visual_qa_'],
  },
  {
    id: 'ship',
    label: 'Ship',
    color: '#06b6d4',  // cyan
    shape: 'diamond',
    prefixes: ['pr_', 'push_', 'evidence_', 'preview_', 'repair_', 'deploy_'],
  },
];

const SKIP_EVENT_TYPES = new Set([
  'workflow_started', 'workflow_completed', 'workflow_failed',
  'signal_received', 'query_executed', 'query_failed',
]);

function classifyEvent(eventType: string): EventGroup | null {
  if (SKIP_EVENT_TYPES.has(eventType)) return null;
  for (const group of EVENT_GROUPS) {
    if (group.prefixes.some(p => eventType.startsWith(p))) return group;
  }
  return null;
}

/* ── Status Colors ──────────────────────────────────────────────── */

const STATUS_COLORS = {
  scheduled: { fill: '#d4d4d4', stroke: '#a3a3a3' },
  started:   { fill: '#93c5fd', stroke: '#3b82f6' },
  completed: { fill: '#86efac', stroke: '#22c55e' },
  failed:    { fill: '#fca5a5', stroke: '#ef4444' },
  'timed-out': { fill: '#fdba74', stroke: '#f97316' },
};

/* ── Helpers ─────────────────────────────────────────────────────── */

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return `${min}m${sec}s`;
}

/** Smart tick interval for activity-level granularity */
function tickInterval(rangeMs: number): number {
  const targets = [
    1_000, 5_000, 10_000, 30_000,               // sub-minute
    60_000, 5 * 60_000, 10 * 60_000, 15 * 60_000, // minutes
    30 * 60_000, 60 * 60_000, 2 * 3600_000,       // hours
  ];
  for (const t of targets) {
    if (rangeMs / t <= 20 && rangeMs / t >= 3) return t;
  }
  return targets[targets.length - 1];
}

function fmtTickLabel(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function truncateLabel(label: string, maxLen: number): string {
  return label.length > maxLen ? label.slice(0, maxLen - 1) + '\u2026' : label;
}

function formatEventTitle(eventType: string): string {
  return eventType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/* ── SVG Marker Shapes ──────────────────────────────────────────── */

function MarkerShape({ x, y, shape, color, size = 4, opacity = 0.85 }: {
  x: number; y: number; shape: EventGroup['shape']; color: string; size?: number; opacity?: number;
}) {
  switch (shape) {
    case 'circle':
      return <circle cx={x} cy={y} r={size * 0.6} fill={color} opacity={opacity} />;
    case 'diamond':
      return (
        <rect
          x={x - size * 0.5} y={y - size * 0.5}
          width={size} height={size}
          fill={color} opacity={opacity}
          transform={`rotate(45 ${x} ${y})`}
        />
      );
    case 'triangle':
      return (
        <polygon
          points={`${x},${y - size * 0.6} ${x - size * 0.55},${y + size * 0.4} ${x + size * 0.55},${y + size * 0.4}`}
          fill={color} opacity={opacity}
        />
      );
    case 'square':
      return (
        <rect
          x={x - size * 0.45} y={y - size * 0.45}
          width={size * 0.9} height={size * 0.9}
          fill={color} opacity={opacity}
          rx={1}
        />
      );
  }
}

/* ── Main Component ─────────────────────────────────────────────── */

interface ClassifiedAnnotation {
  event: UnifiedTimelineEvent;
  group: EventGroup;
}

export default function WorkflowWaterfall({
  activityLifecycles,
  customEvents = [],
  workflowStartTime,
  workflowEndTime,
  className,
  selectedActivityId,
  onSelectActivity,
  issueExecutionMap,
  onNavigateToStory,
  sprintStatus,
  sprintEndTime,
}: WorkflowWaterfallProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(800);
  const [zoom, setZoom] = useState<ZoomLevel>('all');
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: React.ReactNode } | null>(null);
  const [enabledGroups, setEnabledGroups] = useState<Set<string>>(
    () => new Set(EVENT_GROUPS.map(g => g.id))
  );
  const [selectedEventIdx, setSelectedEventIdx] = useState<number | null>(null);

  // Track container width via ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerW(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Toggle a group on/off
  const toggleGroup = useCallback((groupId: string) => {
    setEnabledGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // Classify and filter annotations
  const { annotations, groupCounts } = useMemo(() => {
    const classified: ClassifiedAnnotation[] = [];
    const counts: Record<string, number> = {};
    for (const g of EVENT_GROUPS) counts[g.id] = 0;

    for (const e of customEvents) {
      if (e.source !== 'custom-telemetry') continue;
      const group = classifyEvent(e.eventType);
      if (!group) continue;
      counts[group.id]++;
      classified.push({ event: e, group });
    }
    return { annotations: classified, groupCounts: counts };
  }, [customEvents]);

  // Which groups actually have events?
  const activeGroups = useMemo(
    () => EVENT_GROUPS.filter(g => groupCounts[g.id] > 0),
    [groupCounts]
  );

  // Filtered annotations based on enabled groups
  const visibleAnnotations = useMemo(
    () => annotations.filter(a => enabledGroups.has(a.group.id)),
    [annotations, enabledGroups]
  );

  // Compute full data time domain
  const { dataStartMs, dataEndMs } = useMemo(() => {
    const start = workflowStartTime ? new Date(workflowStartTime).getTime() : Date.now();
    let end = workflowEndTime ? new Date(workflowEndTime).getTime() : Date.now();

    for (const lc of activityLifecycles) {
      const completedTime = lc.completedEvent?.eventTime || lc.failedEvent?.eventTime || lc.timedOutEvent?.eventTime;
      if (completedTime) {
        const t = new Date(completedTime).getTime();
        if (t > end) end = t;
      }
    }

    return { dataStartMs: start, dataEndMs: end || start + 1000 };
  }, [activityLifecycles, workflowStartTime, workflowEndTime]);

  // Compute visible range and scaling (container-relative)
  const timelineW = Math.max(containerW - LEFT_LABEL_WIDTH - PADDING_RIGHT, 400);

  const { rangeStart, rangeEnd } = useMemo(() => {
    const zoomMs = ZOOM_MS[zoom];
    if (zoomMs === null) {
      // "all" — fit everything with 5% padding
      const span = (dataEndMs - dataStartMs) || 1000;
      return {
        rangeStart: dataStartMs - span * 0.05,
        rangeEnd: dataEndMs + span * 0.05,
      };
    }
    // Named zoom: show fixed time window anchored at data start
    return {
      rangeStart: dataStartMs,
      rangeEnd: dataStartMs + zoomMs,
    };
  }, [zoom, dataStartMs, dataEndMs]);

  const pxPerMs = timelineW / ((rangeEnd - rangeStart) || 1);

  // Sort activities chronologically
  const sortedActivities = useMemo(() => {
    return [...activityLifecycles].sort((a, b) =>
      new Date(a.scheduledEvent.eventTime).getTime() - new Date(b.scheduledEvent.eventTime).getTime()
    );
  }, [activityLifecycles]);

  // SVG dimensions
  const contentHeight = sortedActivities.length * (ROW_HEIGHT + ROW_GAP) + TOP_AXIS_HEIGHT + 8;
  const svgWidth = LEFT_LABEL_WIDTH + timelineW + PADDING_RIGHT;
  const svgHeight = Math.max(120, contentHeight);

  // Time-to-X converter using rangeStart + pxPerMs
  const timeToX = useCallback((t: string | number) => {
    const ms = typeof t === 'number' ? t : new Date(t).getTime();
    return LEFT_LABEL_WIDTH + (ms - rangeStart) * pxPerMs;
  }, [rangeStart, pxPerMs]);

  // Smart axis ticks
  const axisTicks = useMemo(() => {
    const interval = tickInterval(rangeEnd - rangeStart);
    const firstTick = Math.ceil(rangeStart / interval) * interval;
    const ticks: { x: number; label: string }[] = [];
    for (let t = firstTick; t <= rangeEnd; t += interval) {
      ticks.push({
        x: LEFT_LABEL_WIDTH + (t - rangeStart) * pxPerMs,
        label: fmtTickLabel(t),
      });
    }
    return ticks;
  }, [rangeStart, rangeEnd, pxPerMs]);

  if (sortedActivities.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4 ${className || ''}`}>
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-3">Activity Waterfall</p>
        <div className="flex items-center justify-center h-24 text-[11px] text-neutral-400">
          No activity executions recorded
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`bg-white rounded-lg border border-neutral-200 shadow-warm-sm ${className || ''}`}>
      {/* Header with controls */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Activity Waterfall</p>
          <span className="text-[10px] font-mono text-neutral-400">{sortedActivities.length} activities</span>
        </div>
        {/* Pill-style zoom toggle */}
        <div className="flex items-center gap-0.5 bg-neutral-100 rounded p-0.5">
          {(Object.keys(ZOOM_MS) as ZoomLevel[]).map((level) => (
            <button
              key={level}
              onClick={() => setZoom(level)}
              className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded transition-colors ${
                zoom === level
                  ? 'bg-neutral-800 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Legend: status bars + toggleable event groups */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 pb-2">
        {/* Bar status legend */}
        {(['scheduled', 'started', 'completed', 'failed'] as const).map(status => (
          <div key={status} className="flex items-center gap-1">
            <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: STATUS_COLORS[status].fill, border: `1px solid ${STATUS_COLORS[status].stroke}` }} />
            <span className="text-[9px] text-neutral-500 capitalize">{status}</span>
          </div>
        ))}

        {/* Separator */}
        {activeGroups.length > 0 && (
          <div className="w-px h-3 bg-neutral-200 mx-1" />
        )}

        {/* Pipeline event group toggles */}
        {activeGroups.map(group => {
          const enabled = enabledGroups.has(group.id);
          const count = groupCounts[group.id];
          return (
            <button
              key={group.id}
              onClick={() => toggleGroup(group.id)}
              className={`
                flex items-center gap-1 px-1.5 py-0.5 rounded transition-all text-[9px]
                ${enabled
                  ? 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100'
                  : 'text-neutral-300 line-through hover:text-neutral-400'
                }
              `}
            >
              <svg width={10} height={10} viewBox="0 0 10 10" className="shrink-0">
                <MarkerShape x={5} y={5} shape={group.shape} color={enabled ? group.color : '#d4d4d4'} size={5} opacity={1} />
              </svg>
              <span>{group.label}</span>
              <span className="font-mono text-[8px] opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Chart container with native horizontal scroll */}
      <div className="relative overflow-x-auto overflow-y-auto max-h-[400px]">
        <svg
          ref={svgRef}
          width={svgWidth}
          height={svgHeight}
          className="select-none"
          onMouseLeave={() => setTooltip(null)}
        >
          {/* Time axis */}
          <g>
            {axisTicks.map((tick, i) => (
              <g key={i}>
                <line x1={tick.x} y1={TOP_AXIS_HEIGHT - 4} x2={tick.x} y2={svgHeight} stroke="#e5e5e5" strokeWidth={0.5} />
                <text x={tick.x} y={TOP_AXIS_HEIGHT - 10} textAnchor="middle" fontSize={9} fill="#a3a3a3" fontFamily="monospace">
                  {tick.label}
                </text>
              </g>
            ))}
            <line x1={LEFT_LABEL_WIDTH} y1={TOP_AXIS_HEIGHT} x2={svgWidth - PADDING_RIGHT} y2={TOP_AXIS_HEIGHT} stroke="#d4d4d4" strokeWidth={1} />
          </g>

          {/* Activity rows */}
          {sortedActivities.map((lc, idx) => {
            const y = TOP_AXIS_HEIGHT + idx * (ROW_HEIGHT + ROW_GAP) + 4;
            const barY = y + 4;
            const barH = ROW_HEIGHT - 8;

            const schedX = timeToX(lc.scheduledEvent.eventTime);
            const startX = lc.startedEvent ? timeToX(lc.startedEvent.eventTime) : schedX;
            const sprintFinished = sprintStatus === 'completed' || sprintStatus === 'failed' || sprintStatus === 'partial';
            const capTime = sprintFinished && sprintEndTime ? new Date(sprintEndTime).getTime() : Date.now();

            const endX = lc.completedEvent
              ? timeToX(lc.completedEvent.eventTime)
              : lc.failedEvent
                ? timeToX(lc.failedEvent.eventTime)
                : lc.timedOutEvent
                  ? timeToX(lc.timedOutEvent.eventTime)
                  : timeToX(capTime);

            const finalStatus = (sprintFinished && lc.status === 'started') ? 'completed' : lc.status;
            const schedColor = STATUS_COLORS.scheduled;
            const runColor = finalStatus === 'failed' ? STATUS_COLORS.failed
              : finalStatus === 'timed-out' ? STATUS_COLORS['timed-out']
              : finalStatus === 'completed' ? STATUS_COLORS.completed
              : STATUS_COLORS.started;

            const isSelected = selectedActivityId === lc.activityId;
            const isDimmed = selectedActivityId != null && !isSelected;

            // Resolve issue details from inputMeta
            const storyId = lc.inputMeta?.storyId;
            const issueExec = storyId ? issueExecutionMap?.get(storyId) : undefined;
            const barLabel = storyId
              ? (issueExec?.issueTitle ? `${storyId} ${issueExec.issueTitle}` : storyId)
              : lc.activityType;

            return (
              <g key={lc.activityId} opacity={isDimmed ? 0.35 : 1} style={{ transition: 'opacity 0.15s' }}>
                {/* Row background */}
                <rect
                  x={0} y={y} width={svgWidth} height={ROW_HEIGHT}
                  fill={isSelected ? 'rgba(217, 127, 111, 0.08)' : 'transparent'}
                  className={`hover:fill-neutral-50/80 ${onSelectActivity || (storyId && onNavigateToStory) ? 'cursor-pointer' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSelectActivity) onSelectActivity(isSelected ? null : lc.activityId);
                    if (storyId && onNavigateToStory) onNavigateToStory(storyId);
                  }}
                  onMouseEnter={(e) => {
                    const rect = svgRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setTooltip({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top - 10,
                      content: (
                        <div className="text-[10px]">
                          <div className="font-semibold text-white">{lc.activityType}</div>
                          {storyId && (
                            <>
                              <div className="text-coral-300 font-mono mt-0.5">{storyId}</div>
                              {issueExec?.issueTitle && (
                                <div className="text-white/90 mt-0.5">{issueExec.issueTitle}</div>
                              )}
                              {issueExec?.epicId && (
                                <div className="text-neutral-400">Epic: {issueExec.epicId}</div>
                              )}
                              {issueExec?.status && (
                                <div className="text-neutral-300">
                                  Status: <span className="capitalize">{issueExec.status}</span>
                                </div>
                              )}
                              {issueExec?.errorMessage && (
                                <div className="text-red-300 mt-0.5 max-w-[250px] truncate">
                                  Error: {issueExec.errorMessage}
                                </div>
                              )}
                              {(issueExec?.commits?.length || issueExec?.filesChanged) ? (
                                <div className="text-neutral-400">
                                  {issueExec.commits?.length || 0} commits · {issueExec.filesChanged || 0} files
                                </div>
                              ) : null}
                            </>
                          )}
                          <div className="text-neutral-300 mt-0.5">Status: <span className="capitalize">{lc.status}</span></div>
                          {lc.totalDurationMs != null && (
                            <div className="text-neutral-300">Duration: {formatDuration(lc.totalDurationMs)}</div>
                          )}
                          {lc.scheduledToStartMs != null && (
                            <div className="text-neutral-400">Queue: {formatDuration(lc.scheduledToStartMs)}</div>
                          )}
                          {lc.startedToCompletedMs != null && (
                            <div className="text-neutral-400">Execution: {formatDuration(lc.startedToCompletedMs)}</div>
                          )}
                        </div>
                      ),
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />

                {/* Activity label */}
                <text
                  x={LEFT_LABEL_WIDTH - 8}
                  y={y + ROW_HEIGHT / 2 + 1}
                  textAnchor="end"
                  fontSize={10}
                  fill={storyId ? '#78350f' : '#525252'}
                  fontFamily="ui-monospace, monospace"
                >
                  {truncateLabel(barLabel, 26)}
                </text>

                {/* Scheduled phase (gray) */}
                {startX > schedX && (
                  <rect
                    x={schedX} y={barY} width={Math.max(MIN_BAR_WIDTH, startX - schedX)} height={barH}
                    rx={3} ry={3}
                    fill={schedColor.fill} stroke={schedColor.stroke} strokeWidth={0.5}
                  />
                )}

                {/* Running/Completed phase */}
                <rect
                  x={startX} y={barY} width={Math.max(MIN_BAR_WIDTH, endX - startX)} height={barH}
                  rx={3} ry={3}
                  fill={runColor.fill}
                  stroke={isSelected ? '#D97F6F' : runColor.stroke}
                  strokeWidth={isSelected ? 1.5 : 0.5}
                  className={onSelectActivity || (storyId && onNavigateToStory) ? 'cursor-pointer' : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSelectActivity) onSelectActivity(isSelected ? null : lc.activityId);
                    if (storyId && onNavigateToStory) onNavigateToStory(storyId);
                  }}
                />

                {/* Pulsing indicator for running */}
                {lc.status === 'started' && !sprintFinished && (
                  <circle cx={endX} cy={barY + barH / 2} r={3} fill={STATUS_COLORS.started.stroke}>
                    <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Pipeline event markers — positioned on the nearest activity row */}
          {visibleAnnotations.map((ann, i) => {
            const x = timeToX(ann.event.timestamp);
            const eventMs = new Date(ann.event.timestamp).getTime();
            const isEventSelected = selectedEventIdx === i;

            // Find nearest activity row by time overlap
            let bestRow = -1;
            let bestDist = Infinity;
            for (let r = 0; r < sortedActivities.length; r++) {
              const lc = sortedActivities[r];
              const s = new Date(lc.scheduledEvent.eventTime).getTime();
              const e = lc.completedEvent ? new Date(lc.completedEvent.eventTime).getTime()
                : lc.failedEvent ? new Date(lc.failedEvent.eventTime).getTime()
                : lc.timedOutEvent ? new Date(lc.timedOutEvent.eventTime).getTime()
                : Date.now();
              if (eventMs >= s && eventMs <= e) { bestRow = r; break; }
              const dist = Math.min(Math.abs(eventMs - s), Math.abs(eventMs - e));
              if (dist < bestDist) { bestDist = dist; bestRow = r; }
            }

            if (bestRow < 0) bestRow = 0;
            const rowY = TOP_AXIS_HEIGHT + bestRow * (ROW_HEIGHT + ROW_GAP) + 4;
            const markerY = rowY + ROW_HEIGHT / 2;

            return (
              <g
                key={`ann-${i}`}
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEventIdx(isEventSelected ? null : i);
                }}
                onMouseEnter={(e) => {
                  const rect = svgRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  setTooltip({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top - 10,
                    content: (
                      <div className="text-[10px] max-w-[250px]">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ann.group.color }} />
                          <span className="font-semibold text-white">{formatEventTitle(ann.event.eventType)}</span>
                        </div>
                        <div className="text-neutral-400 mt-0.5">{ann.group.label} event</div>
                        {ann.event.description && (
                          <div className="text-neutral-300 mt-0.5 truncate">{ann.event.description}</div>
                        )}
                        <div className="text-neutral-500 font-mono mt-0.5 text-[8px]">
                          {new Date(ann.event.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    ),
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
              >
                {/* Selection highlight line */}
                {isEventSelected && (
                  <line
                    x1={x} y1={TOP_AXIS_HEIGHT} x2={x} y2={svgHeight}
                    stroke={ann.group.color} strokeWidth={1} strokeDasharray="3,3" opacity={0.4}
                  />
                )}
                {/* Marker */}
                <MarkerShape
                  x={x} y={markerY}
                  shape={ann.group.shape}
                  color={ann.group.color}
                  size={isEventSelected ? 6 : 4.5}
                  opacity={isEventSelected ? 1 : 0.8}
                />
                {/* Selection ring */}
                {isEventSelected && (
                  <circle cx={x} cy={markerY} r={7} fill="none" stroke={ann.group.color} strokeWidth={1} opacity={0.5} />
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip overlay */}
        {tooltip && (
          <div
            className="absolute pointer-events-none z-20"
            style={{ left: tooltip.x + 12, top: tooltip.y - 8 }}
          >
            <div className="bg-neutral-800 text-white rounded px-2.5 py-1.5 shadow-lg whitespace-nowrap">
              {tooltip.content}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
